<script setup lang="ts">
/**
 * The side panel on the Executions board that talks to a team's lead (#430). It opens the person's own everyday
 * conversation with the lead, or with the COO acting as lead when the team has none (`api.openTeamLeadChat`): the
 * thread the Chat page shows for that member, never a second chat surface with a memory of its own. It sends with the
 * Chat page's own `api.postChatMessage`, reads the thread again while the lead is replying, and tells the board
 * (`changed`) when a reply carries a new call that changed the work list, so the board reads itself again at once.
 *
 * Built from the kit alone: the Sheet, the kit's chat thread, a Textarea and Button for the composer, a Select for the
 * team picker (shown only when the board is on "All teams"), and an empty state for a team nobody can lead.
 */
import {
    Button,
    type ChatEntry,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    Textarea,
    ZhChatThread,
    ZhEmptyState,
} from '@zerohuman/ui'
import { computed, onUnmounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type ChatDetail, type ChatMessageRow, refusalCode, type Team } from '@/api'
import ChatToolLog from '@/components/ChatToolLog.vue'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { awaitingConfirmation, type ChatToolItem, threadEntries, toolPayload, toolRunId } from '@/lib/chat'

const props = withDefaults(
    defineProps<{
        /** Whether the panel is open. */
        open: boolean
        /** Every team, for the team picker. */
        teams: Team[]
        /** The team whose lead's chat the panel opens. */
        teamId: string
        /** Whether the panel offers a team picker: the board is on "All teams", so the team is the panel's to choose. */
        pickTeam?: boolean
    }>(),
    { pickTeam: false },
)
const emit = defineEmits<{
    'update:open': [open: boolean]
    /** The lead's reply carried a work-list tool call that changed the list. */
    changed: []
}>()

/** The work-list calls that change the list. Listing and reading an item change nothing, so the board stays as it is. */
const WORK_LIST_CHANGES = new Set(['os.add_work_item', 'os.update_work_item', 'os.close_work_item'])
/** The refusal of a team with neither a lead nor a COO to act as one. */
const NO_LEAD = 'team_has_no_lead'
/** How often the thread is read again while the lead is replying: as often as the Chat page reads it. */
const REPLY_READ_MS = 1000

/** The team the panel talks about: the board's, or the one picked in the panel. */
const chosen = ref(props.teamId)
watch(
    () => props.teamId,
    id => {
        chosen.value = id
    },
)
const team = computed(() => props.teams.find(row => row.id === chosen.value) ?? null)

const chat = ref<ChatDetail | null>(null)
const loading = ref(false)
/** The code the API refused the open with, when it sent one: `team_has_no_lead` has a screen of its own. */
const refusal = ref<string | null>(null)
/** That the open failed, and why, in words a person reads: never a code. */
const openFailed = ref<string | null>(null)
const draft = ref('')
const sending = ref(false)
const sendError = ref<string | null>(null)
/**
 * What the person just sent, drawn at the end of the thread until the API answers. The send is only answered once
 * the whole reply is done, so without it the thread would show nothing of theirs until the reply lands, as the Chat
 * page avoids with its own outbox.
 */
const outgoing = ref<string | null>(null)

/** What to try when nothing better can be said: most failures here are the connection's. */
const TRY_AGAIN = 'Check your connection and try again.'

/**
 * Why a call failed, in words: the server's own sentence when it sent one, else `TRY_AGAIN`. A bare code
 * (`chat_not_found`) is never shown, nor the browser's own wording for a request that never got an answer.
 */
function inWords(caught: unknown): string {
    if (!(caught instanceof Error) || caught instanceof TypeError) return TRY_AGAIN
    const said = caught.message.trim()
    return /\s/.test(said) ? said : TRY_AGAIN
}

/** Every row already accounted for: a work-list call among them has been told to the board, or was there before. */
const settled = new Set<string>()
/** Each open counts up, so an answer to an earlier one (for another team) never lands over the current one. */
let opens = 0
let replyRead: ReturnType<typeof setTimeout> | null = null

function stopReading(): void {
    if (replyRead !== null) clearTimeout(replyRead)
    replyRead = null
}

/** Takes in the thread as the API answered it, telling the board once when a new row changed the work list. */
function absorb(thread: ChatDetail): void {
    const fresh = thread.messages.filter(row => !settled.has(row.id) && !awaitingConfirmation(row))
    for (const row of fresh) settled.add(row.id)
    chat.value = thread
    if (fresh.some(row => row.role === 'tool' && WORK_LIST_CHANGES.has(row.toolName ?? ''))) emit('changed')
    if (thread.status === 'replying') readWhileReplying()
    else stopReading()
}

/** Reads the thread again in a moment, while the lead is still replying; a failed read is tried again. */
function readWhileReplying(): void {
    stopReading()
    const open = opens
    const id = chat.value?.id ?? ''
    replyRead = setTimeout(async () => {
        replyRead = null
        try {
            const thread = await api.chat(id)
            if (open === opens) absorb(thread)
        } catch {
            if (open === opens) readWhileReplying()
        }
    }, REPLY_READ_MS)
}

async function openChat(teamId: string): Promise<void> {
    const open = ++opens
    stopReading()
    settled.clear()
    chat.value = null
    refusal.value = null
    openFailed.value = null
    sendError.value = null
    // A message still on its way belongs to the conversation it was sent in, not to this one.
    outgoing.value = null
    sending.value = false
    loading.value = true
    try {
        const thread = await api.openTeamLeadChat(teamId)
        if (open !== opens) return
        for (const row of thread.messages) if (!awaitingConfirmation(row)) settled.add(row.id)
        absorb(thread)
    } catch (caught) {
        if (open !== opens) return
        refusal.value = refusalCode(caught)
        openFailed.value = inWords(caught)
    } finally {
        if (open === opens) loading.value = false
    }
}

/** The team the panel is open on, or '' while it is closed: each change opens that team's conversation once. */
const opened = computed(() => (props.open ? chosen.value : ''))
watch(
    opened,
    id => {
        opens += id ? 0 : 1
        stopReading()
        if (id) void openChat(id)
    },
    { immediate: true },
)
onUnmounted(() => {
    opens += 1
    stopReading()
})

function pick(choice: unknown): void {
    if (typeof choice === 'string' && choice !== '') chosen.value = choice
}

async function send(): Promise<void> {
    const thread = chat.value
    const text = draft.value.trim()
    if (!thread || text === '' || sending.value) return
    // #1796: the lead's reply is model work, so a message asks the AI account guard first. A blocked send keeps the
    // draft in the box, and the shell's prompt says how to connect an account.
    if (!(await requireAiAccount())) return
    // Drawn in the thread at once, out of the box, with the lead at work until the reply lands (the Chat page's way).
    const open = opens
    sending.value = true
    sendError.value = null
    outgoing.value = text
    draft.value = ''
    try {
        const answered = await api.postChatMessage(thread.id, text)
        if (open === opens) absorb(answered)
    } catch (caught) {
        // The box is disabled while sending, so it is still empty: what was typed goes back for another try, unless
        // the panel has moved on to another conversation since, where it was never meant to go.
        if (open === opens) {
            draft.value = text
            sendError.value = `We could not send your message. ${inWords(caught)} It is back in the box to send again.`
        }
    } finally {
        if (open === opens) {
            outgoing.value = null
            sending.value = false
        }
    }
}

/** Enter sends, as in the Chat page; Shift+Enter starts a new line. */
function sendOnEnter(event: KeyboardEvent): void {
    if (event.shiftKey || event.isComposing) return
    event.preventDefault()
    void send()
}

const leadName = computed(() => chat.value?.member.name || team.value?.leadName || 'the lead')
/** Whom the thread is with, as its head labels them: the team's lead, or the COO acting as lead. */
const leadRole = computed(() =>
    chat.value && team.value?.leadMemberId === chat.value.memberId ? 'Lead' : 'Acting lead',
)

/** One tool row as the Chat page's tool log shows it. */
function toolItem(row: ChatMessageRow): ChatToolItem {
    return {
        id: row.id,
        tool: row.toolName ?? 'Tool call',
        runId: toolRunId(row),
        slug: null,
        status: row.run?.status ?? null,
        taskLabel: row.run?.taskName ?? null,
        resources: [],
        payload: toolPayload(row),
    }
}

/** The thread as it reads: each message, with what the lead did hanging off the reply it produced. */
const threadView = computed(() => threadEntries(chat.value?.messages ?? []))
const entries = computed<ChatEntry[]>(() => [
    ...threadView.value.map(
        (entry): ChatEntry =>
            entry.kind === 'message' && entry.row.role === 'user'
                ? { kind: 'user', text: entry.row.content ?? '', id: entry.key }
                : { kind: 'assistant', text: entry.kind === 'message' ? (entry.row.content ?? '') : '', id: entry.key },
    ),
    ...(outgoing.value === null ? [] : [{ kind: 'user' as const, text: outgoing.value, id: 'outgoing' }]),
])
const toolsOf = computed(() => new Map(threadView.value.map(entry => [entry.key, entry.tools.map(toolItem)])))
</script>

<template>
    <Sheet :open="open" @update:open="value => emit('update:open', value)">
        <SheetContent class="flex flex-col gap-3">
            <SheetHeader>
                <SheetTitle>Talk to {{ leadName }}</SheetTitle>
                <SheetDescription>This is the same conversation you see on the Chat page. What {{ leadName }} changes on the work list shows on the board, and the items they add wait in the Backlog lane as proposals for you to approve.</SheetDescription>
                <Select v-if="pickTeam" :model-value="chosen" @update:model-value="pick">
                    <SelectTrigger aria-label="Team">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem v-for="row in teams" :key="row.id" :value="row.id">{{ row.name }}</SelectItem>
                    </SelectContent>
                </Select>
            </SheetHeader>
            <div class="min-h-0 flex-1 overflow-y-auto px-4">
                <p v-if="loading" role="status" class="text-muted-foreground text-sm">Opening the conversation…</p>
                <ZhEmptyState v-else-if="refusal === NO_LEAD" label="No lead">
                    {{ team?.name ?? 'This team' }} has no lead yet. Name one on the team's page to chat with them here.
                    <template v-if="team" #action>
                        <RouterLink :to="`/teams/${team.slug}`" class="text-primary text-sm underline-offset-4 hover:underline">Open {{ team.name }}</RouterLink>
                    </template>
                </ZhEmptyState>
                <div v-else-if="openFailed" role="alert" class="text-destructive space-y-2 text-sm">
                    <p>We could not open your chat with {{ leadName }}. {{ openFailed }}</p>
                    <Button type="button" size="sm" variant="outline" @click="openChat(chosen)">Try again</Button>
                </div>
                <ZhChatThread v-else-if="chat" :member="{ name: leadName, avatarUrl: chat.member.avatarUrl }" :role="leadRole" :entries="entries" :thinking="sending || chat.status === 'replying'">
                    <template #message-extra="{ entry }">
                        <ChatToolLog v-if="(toolsOf.get(entry.id ?? '') ?? []).length > 0" :items="toolsOf.get(entry.id ?? '') ?? []" />
                    </template>
                </ZhChatThread>
            </div>
            <div v-if="chat" class="space-y-2 px-4 pb-4" data-testid="lead-chat-composer">
                <Textarea v-model="draft" rows="3" :aria-label="`Message ${leadName}`" placeholder="Add a piece on onboarding" :disabled="sending" @keydown.enter="sendOnEnter" />
                <p v-if="sendError" role="alert" class="text-destructive text-sm">{{ sendError }}</p>
                <div class="flex items-center justify-between gap-2">
                    <p class="text-muted-foreground text-xs" data-testid="lead-chat-keys">Enter sends. Shift+Enter starts a new line.</p>
                    <Button type="button" size="sm" :disabled="sending || draft.trim() === ''" @click="send">{{ sending ? 'Sending…' : 'Send' }}</Button>
                </div>
            </div>
        </SheetContent>
    </Sheet>
</template>
